import { supabase } from "@/integrations/supabase/client";

export class RealtimeChat {
  private pc: RTCPeerConnection | null = null;
  private dc: RTCDataChannel | null = null;
  private audioEl: HTMLAudioElement;

  constructor(private onMessage: (message: any) => void) {
    this.audioEl = document.createElement("audio");
    this.audioEl.autoplay = true;
  }

  async init() {
    // Get ephemeral token via our Supabase Edge Function
    const { data, error } = await supabase.functions.invoke("openai-realtime-session");
    if (error) throw new Error(error.message || "Failed to create realtime session");

    const EPHEMERAL_KEY = data?.client_secret?.value;
    if (!EPHEMERAL_KEY) throw new Error("Missing ephemeral token from session response");

    this.pc = new RTCPeerConnection();

    // Remote audio
    this.pc.ontrack = (e) => {
      this.audioEl.srcObject = e.streams[0];
    };

    // Local microphone
    const ms = await navigator.mediaDevices.getUserMedia({ audio: true });
    this.pc.addTrack(ms.getTracks()[0]);

    // Data channel for events
    this.dc = this.pc.createDataChannel("oai-events");
    this.dc.addEventListener("message", (e) => {
      try {
        const event = JSON.parse(e.data);
        this.onMessage?.(event);
      } catch (err) {
        console.warn("Non-JSON message", e.data);
      }
    });

    // Offer/Answer
    const offer = await this.pc.createOffer();
    await this.pc.setLocalDescription(offer);

    const baseUrl = "https://api.openai.com/v1/realtime";
    const model = "gpt-4o-realtime-preview-2024-12-17";
    const sdpResponse = await fetch(`${baseUrl}?model=${model}`, {
      method: "POST",
      body: offer.sdp,
      headers: {
        Authorization: `Bearer ${EPHEMERAL_KEY}`,
        "Content-Type": "application/sdp",
      },
    });

    const answer = {
      type: "answer" as RTCSdpType,
      sdp: await sdpResponse.text(),
    };

    await this.pc.setRemoteDescription(answer);
  }

  sendMessage(text: string) {
    if (!this.dc || this.dc.readyState !== "open") {
      throw new Error("Voice agent not connected");
    }

    const event = {
      type: "conversation.item.create",
      item: {
        type: "message",
        role: "user",
        content: [
          {
            type: "input_text",
            text,
          },
        ],
      },
    };

    this.dc.send(JSON.stringify(event));
    this.dc.send(JSON.stringify({ type: "response.create" }));
  }

  disconnect() {
    try {
      this.dc?.close();
      this.pc?.close();
    } finally {
      this.dc = null;
      this.pc = null;
    }
  }
}
